<template>
  <div>
    <div v-if="!$vuetify.breakpoint.xs" class="pb-3 flex items-center flex-wrap gap-3">
      <div v-if="!formHasChanges" class="text-primary text-sm font-medium">
        Filters
        <v-icon color="primary" size="12" right>mdi-tune-variant</v-icon>
      </div>
      <div
        v-else
        class="text-[var(--v-persian-red-base)] cursor-pointer text-sm font-medium"
        @click="onResetFilters"
      >
        Reset Filters
        <v-icon color="persian-red" size="12" right>mdi-tune-variant</v-icon>
      </div>
      <slot name="prepend-filters" />
      <template v-if="selectedFilters?.length">
        <div v-for="filter in selectedFilters" :key="filter.value">
          <CfsFilterSelect
            v-if="filter.type === VirtualSearchFilterInputs.SELECT"
            :selected-filters.sync="computedFilterForm[filter.value]"
            :disabled="loading"
            :item-disabled="() => loading"
            :items="filterValues[filter.value]"
            :placeholder="filter.label"
            :filter="
              (item, qeury) => {
                return item.key.toLocaleLowerCase().indexOf(qeury.toLocaleLowerCase()) > -1;
              }
            "
            object-field="key"
            item-value="key"
            :return-object="false"
            outlined
          >
            <template #item="{ item }">
              <div class="flex justify-between">
                <span class="text-sm text-wrap text-uppercase font-medium">{{ item?.key }}</span>
                <v-chip v-if="item?.doc_count" small>{{ item.doc_count }}</v-chip>
              </div>
            </template>
          </CfsFilterSelect>
          <CfsDateField
            v-else-if="filter.type === VirtualSearchFilterInputs.DATE"
            v-model="computedFilterForm[filter.value]"
            floor-range-dates
            range
            :placeholder="filter.label"
          />
          <CfsCheckbox
            v-else-if="filter.type === VirtualSearchFilterInputs.BOOLEAN"
            v-model="computedFilterForm[filter.value]"
            indeterminate
            :disabled="loading"
          >
            <template #label>
              <span class="font-medium text-md text-black-800">
                {{ filter.label }}
              </span>
            </template>
          </CfsCheckbox>
          <CfsTextField
            v-else-if="filter.type === VirtualSearchFilterInputs.TEXT"
            v-model="freeInputForm[filter.value]"
            :placeholder="filter.label"
            :disabled="loading"
            clearable
            hide-details
            @click:clear="updateFreeForm(filter.value, null)"
            @keydown.enter="updateFreeForm(filter.value)"
          />
        </div>
      </template>
      <CfsDropdown
        v-if="filterOptions?.length"
        max-height="400px"
        title="+ More"
        :disabled="loading"
        :show-icon="false"
        :items="filterOptions"
        :close-on-content-click="false"
        @input="filterSearchQuery = ''"
      >
        <template #list="{ items }">
          <v-card>
            <v-card-text class="p-0">
              <CfsTextField
                v-model.trim="filterSearchQuery"
                class="w-full px-2 pt-2"
                placeholder="Search"
              >
                <template #append>
                  <v-icon
                    v-if="!!filterSearchQuery.length"
                    class="cursor-pointer mr-1"
                    @click.stop="filterSearchQuery = ''"
                  >
                    mdi-close
                  </v-icon>
                  <v-icon color="primary">mdi-magnify</v-icon>
                </template>
              </CfsTextField>
              <v-list height="300" class="overflow-auto">
                <v-list-item-group
                  v-model="selectedFilters"
                  :value-comparator="isSelectedFilter"
                  multiple
                  @change="onSelectedFiltersChange"
                >
                  <template v-for="(item, i) in items">
                    <v-list-item
                      v-show="isMatchingQuery(item)"
                      :key="`item-${i}`"
                      class="px-2"
                      :value="item"
                    >
                      <template #default="{ active }">
                        <v-list-item-action class="mx-3">
                          <CfsCheckbox :input-value="active" />
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title class="text-sm font-medium">
                            {{ item.label }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-list-item>
                  </template>
                </v-list-item-group>
              </v-list>
            </v-card-text>
          </v-card>
        </template>
      </CfsDropdown>
    </div>

    <div v-else class="md:hidden m-3 flex justify-end">
      <ModalWrapper v-model="isOpen" has-close-icon>
        <template #actions>
          <div />
        </template>
        <template #header>
          <v-card-title class="mercury justify-between">
            <h3 class="text--text text-2xl font-bold">Filters</h3>
          </v-card-title>
        </template>
        <template #activator="{ on, attrs }">
          <v-icon
            color="primary"
            class="!absolute top-6 filter-button right-6"
            size="24"
            v-bind="attrs"
            v-on="on"
            >mdi-filter-variant</v-icon
          >
        </template>
        <template #content>
          <div class="p-3 h-[80vh] overflow-y-auto bg-white gap-3">
            <div class="flex mb-3 items-center justify-between">
              <div v-if="!formHasChanges" class="text-primary text-sm font-medium">
                Filters
                <v-icon color="primary" size="12" right>mdi-tune-variant</v-icon>
              </div>
              <div
                v-else
                class="text-[var(--v-persian-red-base)] cursor-pointer text-sm font-medium"
                @click="onResetFilters"
              >
                Reset Filters
                <v-icon color="persian-red" size="12" right>mdi-tune-variant</v-icon>
              </div>
              <CfsDropdown
                v-if="filterOptions?.length"
                max-height="400px"
                title="+ More"
                :disabled="loading"
                :show-icon="false"
                :items="filterOptions"
                :close-on-content-click="false"
                @input="filterSearchQuery = ''"
              >
                <template #list="{ items }">
                  <v-card>
                    <v-card-text class="p-0">
                      <CfsTextField
                        v-model.trim="filterSearchQuery"
                        class="w-full px-2 pt-2"
                        placeholder="Search"
                      >
                        <template #append>
                          <v-icon
                            v-if="!!filterSearchQuery.length"
                            class="cursor-pointer mr-1"
                            @click.stop="filterSearchQuery = ''"
                          >
                            mdi-close
                          </v-icon>
                          <v-icon color="primary">mdi-magnify</v-icon>
                        </template>
                      </CfsTextField>
                      <v-list height="300" class="overflow-auto">
                        <v-list-item-group
                          v-model="selectedFilters"
                          :value-comparator="isSelectedFilter"
                          multiple
                          @change="onSelectedFiltersChange"
                        >
                          <template v-for="(item, i) in items">
                            <v-list-item
                              v-show="isMatchingQuery(item)"
                              :key="`item-${i}`"
                              class="px-2"
                              :value="item"
                            >
                              <template #default="{ active }">
                                <v-list-item-action class="mx-3">
                                  <CfsCheckbox :input-value="active" />
                                </v-list-item-action>
                                <v-list-item-content>
                                  <v-list-item-title class="text-sm font-medium">
                                    {{ item.label }}
                                  </v-list-item-title>
                                </v-list-item-content>
                              </template>
                            </v-list-item>
                          </template>
                        </v-list-item-group>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </template>
              </CfsDropdown>
            </div>

            <div v-if="$slots.prependFilters" class="flex flex-col gap-3">
              <slot name="prependFilters" />
            </div>

            <div v-if="selectedFilters?.length" class="flex flex-col gap-3">
              <div v-for="filter in selectedFilters" :key="filter.value">
                <CfsFilterSelect
                  v-if="filter.type === VirtualSearchFilterInputs.SELECT"
                  :selected-filters.sync="computedFilterForm[filter.value]"
                  :disabled="loading"
                  :item-disabled="() => loading"
                  :items="filterValues[filter.value]"
                  :placeholder="filter.label"
                  :filter="
                    (item, qeury) => {
                      return item.key.toLocaleLowerCase().indexOf(qeury.toLocaleLowerCase()) > -1;
                    }
                  "
                  object-field="key"
                  item-value="key"
                  :return-object="false"
                  outlined
                  class=""
                >
                  <template #item="{ item }">
                    <div class="flex justify-between">
                      <span class="text-sm text-wrap text-uppercase font-medium">{{
                        item?.key
                      }}</span>
                      <v-chip v-if="item?.doc_count" small>{{ item.doc_count }}</v-chip>
                    </div>
                  </template>
                </CfsFilterSelect>
                <CfsDateField
                  v-else-if="filter.type === VirtualSearchFilterInputs.DATE"
                  v-model="computedFilterForm[filter.value]"
                  floor-range-dates
                  range
                  :placeholder="filter.label"
                />
                <CfsCheckbox
                  v-else-if="filter.type === VirtualSearchFilterInputs.BOOLEAN"
                  v-model="computedFilterForm[filter.value]"
                  indeterminate
                  :disabled="loading"
                >
                  <template #label>
                    <span class="font-medium text-md text-black-800">
                      {{ filter.label }}
                    </span>
                  </template>
                </CfsCheckbox>
                <CfsTextField
                  v-else-if="filter.type === VirtualSearchFilterInputs.TEXT"
                  v-model="freeInputForm[filter.value]"
                  :placeholder="filter.label"
                  :disabled="loading"
                  clearable
                  hide-details
                  @click:clear="updateFreeForm(filter.value, null)"
                  @keydown.enter="updateFreeForm(filter.value)"
                />
              </div>
            </div>
          </div>
        </template>
      </ModalWrapper>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, defineAsyncComponent } from 'vue';
import { VirtualSearchResponse } from '@/types/virtual-search';
import {
  AggregationTranslation,
  AggregationTranslationValue,
} from '@/types/virtual-search/filters';
import {
  filterInitialSelectedFilters,
  reduceAggregationFilterOptions,
  reduceAggregationFilterValues,
} from '@/helpers/virtual-search';
import { VirtualSearchFilterInputs } from '@/constants/virtual-search';
import { deepEqual } from '@/helpers/objects';
import { diff } from 'deep-object-diff';
import { useDebouncedRef } from '@/composables/useDebouncedRef';

export default defineComponent({
  name: 'CfsVirtualFilters',
  props: {
    aggregations: {
      type: Object as PropType<VirtualSearchResponse['aggregations']>,
      default: () => ({}),
    },
    translations: {
      type: Object as PropType<AggregationTranslation>,
      default: () => ({}),
      required: true,
    },
    additionalOptions: {
      type: Object as PropType<AggregationTranslation>,
      default: () => ({}),
    },
    nonAggregationKeys: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    filterForm: {
      type: Object,
      default: () => ({}),
    },
    initialForm: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      defaut: false,
    },
  },
  components: {
    ModalWrapper: defineAsyncComponent(() => import('@/components/modal/ModalWrapper.vue')),
  },
  emits: ['update:filter-form', 'reset-filters'],
  setup(props, { emit }) {
    const computedFilterForm = computed({
      get() {
        return props.filterForm;
      },
      set(value) {
        emit('update:filter-form', value);
      },
    });
    const isOpen = ref(false);

    const freeInputForm = ref<Record<string, string>>({});

    const clean = (obj: Record<string, any>) => {
      const result: Record<string, any> = {};
      Object.keys(obj || {}).forEach((key) => {
        const value = obj[key];
        if (value !== '' && value !== null && !(Array.isArray(value) && value.length === 0)) {
          result[key] = value;
        }
      });
      return result;
    };

    const formHasChanges = computed<boolean>(() => {
      const cleanedCurrent = clean(computedFilterForm.value);
      const cleanedInitial = clean(props.initialForm);
      return !!Object.keys(diff(cleanedCurrent, cleanedInitial)).length;
    });

    const selectedFilters = ref(
      filterInitialSelectedFilters(props.initialForm, {
        ...props.translations,
        ...props.additionalOptions,
      })
    );

    const filterSearchQuery = useDebouncedRef<string>('', 100);

    const filterOptions = computed(() => {
      const options = Object.values(
        reduceAggregationFilterOptions(
          props.aggregations,
          props.translations,
          props.additionalOptions
        )
      ).sort((a, b) => a.label.localeCompare(b.label));

      return options;
    });
    const isMatchingQuery = (option: AggregationTranslationValue): boolean =>
      option?.label.toLocaleLowerCase().includes(filterSearchQuery.value.toLocaleLowerCase());

    const filterValues = computed(() => reduceAggregationFilterValues(props.aggregations));
    const onResetFilters = () => {
      computedFilterForm.value = props.initialForm;
      freeInputForm.value = {};
      selectedFilters.value = filterInitialSelectedFilters(props.initialForm, {
        ...props.translations,
        ...props.additionalOptions,
      });
      emit('reset-filters');
    };

    const onSelectedFiltersChange = (filters: any[]) => {
      const filteredForm = Object.keys(computedFilterForm.value)
        .filter((key) => {
          return (
            props.nonAggregationKeys.includes(key) ||
            filters.find((filter) => filter?.value === key)
          );
        })
        .reduce((cur, key) => {
          return Object.assign(cur, { [key]: computedFilterForm.value?.[key] });
        }, {});

      !deepEqual(filteredForm, computedFilterForm.value) &&
        (computedFilterForm.value = filteredForm);
    };

    const isSelectedFilter = (selected: any, item: any) => {
      return selected?.value === item?.value;
    };

    const updateFreeForm = (property: string, value?: any) => {
      computedFilterForm.value = {
        ...computedFilterForm.value,
        [property]:
          value !== undefined
            ? value
            : (freeInputForm.value?.[property].split(',') || []).map((item: string) => item.trim()),
      };
    };
    return {
      formHasChanges,
      computedFilterForm,
      selectedFilters,
      filterOptions,
      filterValues,
      filterSearchQuery,
      freeInputForm,

      isMatchingQuery,
      isSelectedFilter,
      onSelectedFiltersChange,
      onResetFilters,

      updateFreeForm,

      VirtualSearchFilterInputs,
      isOpen,
    };
  },
});
</script>
